import { ChangeDetectionStrategy, Component, ElementRef, input, output, viewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

/**
 * The Figma "File upload · Type=Button" container (guid 787:36856): a tinted panel with the field
 * label and a hint on the left, an upload button on the right, and the uploaded-file cards
 * projected underneath.
 *
 * Deliberately separate from `app-upload-dropzone`, which is the `Type=Dropzone` variant of the
 * same design-system component. That one is consumed by the document card — and through it by the
 * required-documents checklist and readonly-onboarding — which must not move when this panel
 * changes. Both share `upload-validation.util.ts`, so the rules they enforce stay identical.
 *
 * Presentational only: validation, upload and error reporting stay with the consumer.
 */
@Component({
  selector: 'app-upload-panel',
  imports: [MatIconModule],
  template: `
    <!-- aria-disabled next to the class: the disabled look was CSS-only, so assistive tech saw a
         fully active panel (and axe, rightly, judged the dimmed text as a contrast failure rather
         than an inactive control, which WCAG 1.4.3 exempts). -->
    <div
      class="dd-upload-panel rounded-lg p-3"
      [class.dd-upload-panel--disabled]="disabled()"
      [attr.aria-disabled]="disabled() ? 'true' : null">
      <div class="flex flex-col gap-4">
        <div class="dd-upload-panel__header flex items-center justify-between gap-12">
          <div class="flex min-w-0 flex-col gap-0.5">
            @if (label()) {
              <span class="text-[14px] font-medium leading-[1.4]">{{ label() }}{{ required() ? ' *' : '' }}</span>
            }
            @if (hint()) {
              <span class="text-[12px] leading-[1.4] text-accent">{{ hint() }}</span>
            }
          </div>
          <button
            type="button"
            class="dd-upload-panel__button flex h-8 flex-none items-center gap-2 rounded-lg px-3 text-[14px] font-medium"
            [disabled]="disabled()"
            [attr.aria-describedby]="label() ? descriptionId : null"
            (click)="openFilePicker()"
            data-cy="upload-panel-button">
            <mat-icon class="icon-size-4" svgIcon="heroicons_outline:arrow-up-tray"></mat-icon>
            <span>{{ buttonLabel() }}</span>
          </button>
        </div>

        <ng-content />

        @if (error()) {
          <!--
            Figma keeps the message inside the panel and leaves the border alone; per the #20818
            state matrix the border only turns red once the control has focus.
          -->
          <span
            class="text-[14px] leading-[1.4] text-[var(--dd-field-fg-error)]"
            role="alert"
            data-cy="upload-panel-error">
            {{ error() }}
          </span>
        }
      </div>
    </div>
    <input
      #fileInput
      type="file"
      class="hidden"
      [accept]="accept()"
      [disabled]="disabled()"
      (change)="onFileInputChange($event)" />
    @if (label()) {
      <!--
        Per-field context for the button, announced AFTER its own accessible name via
        aria-describedby rather than replacing it via aria-label: WCAG 2.5.3 (Label in Name)
        requires the accessible name to contain the visible label text.
      -->
      <span class="sr-only" [id]="descriptionId">{{ label() }}</span>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadPanelComponent {
  readonly label = input<string>('');
  readonly required = input<boolean>(false);
  /** Second line under the label, e.g. "PDF, PNG of JPEG • tot 20 MB". */
  readonly hint = input<string>('');
  readonly buttonLabel = input<string>('Bestand uploaden');
  /** `accept` attribute for the hidden picker. */
  readonly accept = input<string>('');
  /**
   * Dims the header and blocks the picker, while the cards below stay at full contrast: an
   * attached file is real content and a disabled form is no reason to make it hard to read
   * (WCAG 1.4.3).
   */
  readonly disabled = input<boolean>(false);
  /** Message at the bottom of the panel; empty hides it. */
  readonly error = input<string>('');

  readonly filesSelected = output<FileList>();

  /** Unique per instance so several panels on one page don't collide. */
  protected readonly descriptionId = `upload-panel-desc-${crypto.randomUUID()}`;

  // Angular's compiler cannot resolve a signal-query field declared with native ES `#private`
  // syntax (it needs class-field metadata it can't extract from a true private field) — use TS
  // `private`, matching upload-dropzone.component.ts.
  private readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  protected openFilePicker(): void {
    if (this.disabled()) return;
    this.fileInput()?.nativeElement.click();
  }

  protected onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (this.disabled()) return;
    if (input.files?.length) {
      this.#emitDetached(input.files);
    }
    // Reset so re-picking the same file fires change again.
    input.value = '';
  }

  /**
   * Hand on a copy that is owned by no element. `input.files` stays live-bound to the input, so
   * resetting the element would empty the list again before the consumer's create-then-upload
   * chain reads files[0] — the multipart request would go out without a file.
   */
  #emitDetached(files: FileList): void {
    const snapshot = new DataTransfer();
    for (const file of Array.from(files)) {
      snapshot.items.add(file);
    }
    this.filesSelected.emit(snapshot.files);
  }
}
